/**
 * @file: Which moment the time grid shows at a point on screen.
 *
 * A right-click gives pixels. The panel needs a time. The free core of
 * FullCalendar has no function for this conversion, so the code measures the
 * grid. There is one row for each slot and one column for each day.
 *
 * The measurement does not use the calendar options. The axis width follows its
 * content. The row height follows the zoom level. The body has a scrollbar that
 * the header does not have.
 *
 * yAtTime does the opposite conversion. It gives the position where the grid
 * draws a moment. The on-screen ribbon needs this position to draw a run over
 * the correct rows.
 *
 * The arithmetic is separate from the DOM read. You can therefore test the
 * arithmetic without a layout engine.
 *
 * All boxes in this file use viewport coordinates. getBoundingClientRect
 * reports this frame, and the clientX and clientY of a mouse event use the same
 * frame. The two values compare directly, with no origin to subtract.
 */

import { MINUTES_IN_DAY } from 'common/date-time'

/** How far past the last row still counts as a click on that row. */
const EDGE_TOLERANCE = 2

/**
 * A drag and a resize snap to the quarter hour. A right-click and the default
 * times of a new interval use the same grid.
 */
export const SNAP_MINUTES = 15

/** Convert 'HH:mm:ss' to minutes past midnight. Give null for an unreadable time. */
function minutesOfDay (time) {
  const parts = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(String(time || ''))

  if (!parts) {
    return null
  }

  return Number(parts[1]) * 60 + Number(parts[2]) + Number(parts[3] || 0) / 60
}

/** Give the slot rows in draw order. Remove a row that cannot be read. */
function usableRows (slots) {
  return (slots || [])
    .map(slot => ({
      minutes: minutesOfDay(slot && slot.time),
      top: Number(slot && slot.top),
      height: Number(slot && slot.height)
    }))
    .filter(row => row.minutes !== null && isFinite(row.top) && row.height > 0)
    .sort((one, other) => one.top - other.top)
}

/**
 * How many minutes one row covers.
 *
 * The value comes from an adjacent row, not from slotDuration. A zoom level
 * that is not in the option list is therefore also measured correctly.
 */
function rowSpan (rows, index) {
  const next = rows[index + 1]
  const previous = rows[index - 1]
  const span = next
    ? next.minutes - rows[index].minutes
    : (previous ? rows[index].minutes - previous.minutes : 0)

  return span > 0 ? span : 0
}

/** Give the day column at an x position. Give null for a point beside the grid. */
function columnAt (columns, x) {
  return (columns || []).find(column => {
    const left = Number(column && column.left)
    const width = Number(column && column.width)

    return isFinite(left) && width > 0 && x >= left && x < left + width
  }) || null
}

/**
 * Minutes past midnight at a y position.
 *
 * The result stays in the drawn range. A y above the first row gives the time
 * of the first row. A y below the last row gives the time of the last row. The
 * grid shows no time outside its rows. The code does not calculate a time
 * outside this range.
 */
function minutesAt (rows, y) {
  if (rows.length === 0) {
    return null
  }

  const first = rows[0]
  const last = rows[rows.length - 1]

  if (y < first.top) {
    return first.minutes
  }

  if (y >= last.top + last.height) {
    return last.minutes
  }

  let index = 0
  for (let candidate = 0; candidate < rows.length; candidate++) {
    if (y >= rows[candidate].top) {
      index = candidate
    }
  }

  const row = rows[index]
  const into = Math.min(Math.max((y - row.top) / row.height, 0), 1)

  return row.minutes + into * rowSpan(rows, index)
}

/** Convert a date string and minutes past midnight to a local Date. */
function dateAt (date, minutes) {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(date || ''))

  if (!parts) {
    return null
  }

  const at = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]), 0, 0, 0, 0)
  at.setMinutes(minutes)

  return at
}

/** Give the midnight of a date, as a local time. */
function startOfDay (at) {
  return new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime()
}

/**
 * Minutes past the midnight of a column, by the wall clock.
 *
 * The row labels show wall-clock times, so the offset uses the same method. The
 * code counts whole days by the calendar, then the clock time inside the day. A
 * count of elapsed milliseconds puts every time one hour wrong on the two days
 * each year when the clock changes.
 *
 * A moment on the next day gives a value above 1440. A run that ends at
 * midnight therefore reaches the bottom of the last row, and not the top of the
 * first row.
 */
function minutesFromMidnight (date, at) {
  // The test is explicit because new Date(null) gives the epoch, and not an
  // unreadable date.
  if (at === null || at === undefined) {
    return null
  }

  const midnight = dateAt(date, 0)
  const moment = at instanceof Date ? new Date(at.getTime()) : new Date(at)

  if (!midnight || !isFinite(moment.getTime())) {
    return null
  }

  const days = Math.round((startOfDay(moment) - midnight.getTime()) / 86400000)

  return days * MINUTES_IN_DAY + moment.getHours() * 60 + moment.getMinutes() +
    moment.getSeconds() / 60
}

/**
 * Where a moment falls inside one day column, as a share of that column.
 *
 * The strip below the grid places a run by this share. It read the elapsed
 * milliseconds of the day before, which is 23 or 25 hours on the two days each
 * year when the clock changes. The grid draws its rows by the wall clock, so on
 * those two days the strip and the bars above it disagreed.
 *
 * A moment on the next day gives a share above 1. A run that ends at midnight
 * therefore reaches the right edge of the column instead of the left edge of
 * the next one.
 *
 * @param {string} date - 'YYYY-MM-DD', the day column
 * @param {Date|number|string} at - The moment
 * @returns {number|null} The share, or null for an unreadable date or moment
 */
export function fractionOfDay (date, at) {
  const minutes = minutesFromMidnight(date, at)

  return minutes === null ? null : minutes / MINUTES_IN_DAY
}

/**
 * Give the position where the grid draws a moment.
 *
 * This is the opposite conversion to the vertical part of timeAtPoint.
 *
 * The result stays in the drawn area, in the same way as timeAtPoint. A moment
 * above the first row gives the top edge of that row. A moment below the last
 * row gives the bottom edge of that row. The code therefore draws a run that
 * goes past an end of the grid to the edge, and not outside the grid.
 *
 * @param {Array} slots - [{ time: 'HH:mm:ss', top, height }], viewport frame
 * @param {string} date - 'YYYY-MM-DD', the day column of the moment
 * @param {Date|number|string} at - The moment
 * @returns {number|null} Viewport y. Null for no usable rows, an unreadable
 *   date, or an unreadable moment
 */
export function yAtTime (slots, date, at) {
  const rows = usableRows(slots)

  if (rows.length === 0) {
    return null
  }

  const minutes = minutesFromMidnight(date, at)

  if (minutes === null) {
    return null
  }

  const first = rows[0]
  const last = rows[rows.length - 1]
  const top = first.top
  const bottom = last.top + last.height

  if (minutes <= first.minutes) {
    return top
  }

  let index = 0
  for (let candidate = 0; candidate < rows.length; candidate++) {
    if (minutes >= rows[candidate].minutes) {
      index = candidate
    }
  }

  const row = rows[index]
  const span = rowSpan(rows, index)
  const into = span > 0 ? Math.min(Math.max((minutes - row.minutes) / span, 0), 1) : 0

  return Math.min(Math.max(row.top + into * row.height, top), bottom)
}

/**
 * The moment the grid draws at a point.
 *
 * @param {Array} slots - [{ time: 'HH:mm:ss', top, height }], viewport frame
 * @param {Array} columns - [{ date: 'YYYY-MM-DD', left, width }], viewport frame
 * @param {Object} point - { x, y }, a mouse event's clientX and clientY
 * @returns {Date|null} Rounded down to the quarter hour. Null when the point is
 *   beside the day columns, or when the input cannot be measured
 */
export function timeAtPoint (slots, columns, point) {
  if (!point || !isFinite(Number(point.x)) || !isFinite(Number(point.y))) {
    return null
  }

  const column = columnAt(columns, Number(point.x))

  if (!column) {
    return null
  }

  const rows = usableRows(slots)
  const last = rows[rows.length - 1]

  // The grid's drawn area ends at the last row. minutesAt clamps a point below
  // it to that row's time, which is right for drawing a run that reaches past
  // the end. It is wrong for a right-click: the blank space under the grid is
  // not part of any day, and a menu there offered to create an interval at the
  // last row's time. The tolerance covers a click on the row's own bottom edge.
  if (last && Number(point.y) > last.top + last.height + EDGE_TOLERANCE) {
    return null
  }

  const minutes = minutesAt(rows, Number(point.y))

  if (minutes === null) {
    return null
  }

  return dateAt(column.date, Math.floor(minutes / SNAP_MINUTES) * SNAP_MINUTES)
}

/**
 * Measure the slot rows.
 *
 * Each drawn row has two cells with the same data-time. One cell is the label
 * in the axis, and the other cell is the lane beside it. Both cells are in the
 * same row, so the first cell of each time is sufficient.
 */
export function readSlotBoxes (calendarEl) {
  const seen = new Set()

  return Array.from(calendarEl.querySelectorAll('.fc-timegrid-slot[data-time]'))
    .reduce((boxes, cell) => {
      const time = cell.getAttribute('data-time')

      if (seen.has(time)) {
        return boxes
      }

      seen.add(time)

      const box = cell.getBoundingClientRect()
      boxes.push({ time, top: box.top, height: box.height })

      return boxes
    }, [])
}

/**
 * The element that the grid body scrolls in.
 *
 * The zoom step and the measuring code need the same box. Three copies of
 * this selector chain existed, and the second selector is the
 * fallback for a FullCalendar layout that gives the body no liquid scroller.
 *
 * @param {HTMLElement} calendarEl - The calendar's own element
 * @returns {HTMLElement|null} The scroller, or null when the grid is not drawn
 */
export function readScroller (calendarEl) {
  if (!calendarEl) {
    return null
  }

  return calendarEl.querySelector('.fc-scroller-liquid-absolute') ||
    calendarEl.querySelector('.fc-timegrid-body')?.closest('.fc-scroller') ||
    null
}

/** Measure the day columns. The time axis is not a day. */
export function readColumnBoxes (calendarEl) {
  return Array.from(calendarEl.querySelectorAll('.fc-timegrid-col[data-date]'))
    .filter(column => !column.classList.contains('fc-timegrid-axis'))
    .map(column => {
      const box = column.getBoundingClientRect()

      return { date: column.getAttribute('data-date'), left: box.left, width: box.width }
    })
}
